import {css} from 'styled-components';
import common, {spacing} from '../../common';
import {ThemeSchema} from '../../types';

const typeScale = {
  1: '3.25rem',
  2: '2.25rem',
  3: '1.75rem',
  4: '1.5rem',
  5: '1.125rem',
  6: '1rem',
  7: '.75rem'
};

const lineHeights = [1.28, 1.5];

const tracking = {
  normal: 'normal',
  caps: '1.5px'
};

const fonts = {
  ciutadella: '"Ciutadella", sans-serif'
};

const colors = {
  brand: {
    base: '#e30000',
    light: '#e00',
    dark: '#e00'
  },
  accent: '#09afbb',
  shades: {
    black: '#1e1e1e',
    darkest: '#323232',
    darker: '#555',
    dark: '#7d7d7d',
    light: '#aaa',
    lighter: '#f9f3e9',
    lightest: '#f5f5f5',
    nearWhite: '#fafafa',
    white: '#fff'
  },
  focus: '#8de2e0',
  success: '#65A74D',
  error: '#ed710b',
  info: '#005bb9',
  product: {
    hospital: '#323232',
    extras: '#323232',
    combined: '#323232',
    highlight: {
      backgroundColor: '#bff4f2',
      textColor: '#323232'
    }
  }
};

const focus = {
  outlineColor: colors.focus,
  outlineStyle: 'solid',
  outlineWidth: '3px',
  outlineOffset: '0.125em'
};

const baseButtonStyles = css`
  box-sizing: border-box;
  display: inline-block;
  align-items: center;
  cursor: pointer;
  min-height: 48px;
  border-radius: 4px;
  font-family: ${fonts.ciutadella};
  font-weight: 700;
  letter-spacing: ${tracking.caps};
  text-decoration: none;
  text-transform: uppercase;
  transition-duration: 0.25s;
  transition-timing-function: ease-in-out;
  transition-property: background, border-color;
  border: 2px solid ${colors.brand.base};

  &:hover {
    border-color: #ae0000;
  }

  &:disabled {
    background-color: #ddd;
    border-color: #ddd;
    pointer-events: none;
    color: #999;
  }

  &:focus {
    outline-width: ${focus.outlineWidth};
    outline-style: ${focus.outlineStyle};
    outline-color: ${focus.outlineColor};
    outline-offset: ${focus.outlineOffset};
  }
`;

const buttonStyles = {
  primary: {
    normal: css`
      ${baseButtonStyles}
      background-color: ${colors.brand.base};
      color: ${colors.shades.white};

      &:hover {
        background-color: #ae0000;
      }
      
      &:active {
        background-color: #ae0000;
        border-color: #ae0000;
      }

      &:disabled {
        
      }
    `,
    selected: css`
      background-color: #ae0000;
      border-color: #ae0000;
    `,
    disabled: css``
  },
  secondary: {
    normal: css`
      ${baseButtonStyles}
      background: transparent;
      color: ${colors.brand.base};

      &:hover {
        color: #ae0000;
        border-color: #ae0000;
      }

      &:active {
        color: #ae0000;
        border-color: #ae0000;
      }

      &:disabled {
      }
    `,
    selected: css`
      color: #ae0000;
      border-color: #ae0000;
    `,
    disabled: css``
  },
  small: css`
    padding: ${spacing[2]} ${spacing[4]};
    font-size: ${typeScale[7]};
    min-height: 36px;
  `,
  medium: css`
    padding: ${spacing[2]} ${spacing[6]};
    font-size: ${typeScale[6]};
  `,
  large: css`
    padding: ${spacing[4]} ${spacing[6]};
    font-size: ${typeScale[5]};
  `
};

const baseFormValues = {
  borderColor: colors.shades.dark,
  borderColorValid: colors.shades.dark, //no valid styling
  borderColorInvalid: '#ed710b',
  selectedToggleColor: colors.brand.base,
  helpTextColor: colors.shades.darkest,
  helpTextSize: '18px',
  labelColor: colors.shades.darkest,
  labelSize: '22px',
  errorMessageTextColor: '#ed710b',
  infoMessageTextColor: '#005bb9'
};

const qantas: ThemeSchema = {
  ...common,
  typeScale,
  fonts,
  colors,
  focus,

  id: 'qantas',
  mode: {
    form: 'light' //default
  },

  headings: {
    color: 'currentColor',
    h1: {
      fontFamily: fonts.ciutadella,
      fontSize: typeScale[1],
      lineHeight: lineHeights[0],
      fontWeight: 400,
      tracking: tracking.normal,
      uppercase: false
    },
    h2: {
      fontFamily: fonts.ciutadella,
      fontSize: typeScale[2],
      lineHeight: lineHeights[0],
      fontWeight: 400,
      tracking: tracking.normal,
      uppercase: false
    },
    h3: {
      fontFamily: fonts.ciutadella,
      fontSize: typeScale[3],
      lineHeight: lineHeights[0],
      fontWeight: 400,
      tracking: tracking.normal,
      uppercase: false
    },
    h4: {
      fontFamily: fonts.ciutadella,
      fontSize: typeScale[4],
      lineHeight: lineHeights[0],
      fontWeight: 400,
      tracking: tracking.normal,
      uppercase: false
    },
    h5: {
      fontFamily: fonts.ciutadella,
      fontSize: typeScale[5],
      lineHeight: lineHeights[0],
      fontWeight: 400,
      tracking: tracking.normal,
      uppercase: false
    },
    h6: {
      fontFamily: fonts.ciutadella,
      fontSize: typeScale[6],
      lineHeight: lineHeights[0],
      fontWeight: 400,
      tracking: tracking.normal,
      uppercase: false
    }
  },

  copy: {
    color: colors.shades.darkest,
    fontFamily: fonts.ciutadella,
    fontSize: '18px',
    fontWeight: 400,
    lineHeight: lineHeights[1],
    smallFontSize: '15px',
    largeFontSize: '20px'
  },

  link: {
    loudColor: colors.brand.base,
    darkColor: colors.shades.darkest,
    lightColor: colors.shades.white
  },

  button: {
    small: buttonStyles.small,
    medium: buttonStyles.medium,
    large: buttonStyles.large,
    primary: {
      dark: buttonStyles.primary,
      light: buttonStyles.primary
    },
    secondary: {
      dark: buttonStyles.secondary,
      light: buttonStyles.secondary
    }
  },

  smallFooter: {
    backgroundColor: colors.shades.darkest,
    textColor: colors.shades.lightest,
    linkColor: colors.shades.lightest
  },

  forms: {
    light: {
      ...baseFormValues,
      backgroundColor: colors.shades.lightest,
      inputBackgroundColor: colors.shades.white,
      boxShadow: `0 4px 8px 0 ${colors.shades.lighter}`
    },
    white: {
      ...baseFormValues,
      backgroundColor: colors.shades.white,
      inputBackgroundColor: colors.shades.lightest,
      boxShadow: 'none'
    }
  }
};

export default qantas;
